import React, { useEffect, useState } from "react";
import { Calendar, User, Clock, ChevronRight, Sparkles } from "lucide-react";
import microplasticImg from "../assets/images/microplastic.png";
import edibleCupImg from "../assets/images/edible_cups.jpg";

const Blog = () => {
  const [isCollaborationVisible, setIsCollaborationVisible] = useState(false);
  const [expandedPostId, setExpandedPostId] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const section = document.getElementById("collaboration-section");
      if (section) {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          setIsCollaborationVisible(true);
        }
      }
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleExpand = (postId) => {
    setExpandedPostId(expandedPostId === postId ? null : postId);
  };

  const blogPosts = [
    {
      id: 1,
      title:
        "Microplastics in Disposable Paper Cups: Hidden Risks in Hot Drinks",
      excerpt:
        "Research shows that disposable paper cups shed microplastics and release harmful chemicals when exposed to hot liquids, posing potential health risks.",
      category: "Technology",
      author: "Magomeal Team",
      date: "November 23, 2025",
      readTime: "5 min read",
      featured: true,
      content: (
        <div>
          <p>
            Most disposable paper cups aren’t just paper. To prevent leaks, their
            interiors are coated with a thin plastic lining. Studies show that
            when hot drinks are poured into these cups, the lining can release
            tens of thousands of microplastic particles along with chemicals like
            fluoride, chloride, and even trace metals such as lead, chromium, and
            cadmium.
          </p>

          <img
            src={microplasticImg}
            alt="Microplastics in paper cup"
            style={{
              width: "100%",
              margin: "1rem 0",
              borderRadius: "8px",
            }}
          />

          <p>
            These microplastics and chemicals are invisible to the eye but
            represent real exposure every time you drink hot beverages. The
            long-term health effects are still under research, but evidence
            suggests potential risks including inflammation, cellular stress,
            and accumulation in body tissues.
          </p>

          <p>
            Choosing reusable cups or edible alternatives reduces exposure to
            these contaminants and supports sustainability.
          </p>
        </div>
      ),
    },

    {
      id: 2,
      title: "Are Edible Cups Safe? What They’re Made Of and Why They Matter",
      excerpt:
        "Edible cups are emerging as a practical alternative to plastic-lined paper cups. This article explains what they are made of, how they perform with hot drinks, and why they matter.",
      category: "Technology",
      author: "Magomeal Team",
      date: "December 29, 2025",
      readTime: "4 min read",
      featured: true,
      content: (
        <div>
          <p>
            As awareness grows around microplastics and chemical leaching from
            disposable paper cups, edible cups are gaining attention as a safer
            alternative. A natural question follows: are edible cups actually
            safe?
          </p>

          <img
            src={edibleCupImg}
            alt="Edible cup for hot drinks"
            style={{
              width: "100%",
              margin: "1rem 0",
              borderRadius: "8px",
            }}
          />

          <p>
            Edible cups are made from food-grade ingredients such as grains,
            bran, and plant-based oils. They contain no plastic linings or
            synthetic coatings. Everything touching the drink is designed to be
            consumed.
          </p>

          <p>
            Properly designed edible cups can hold hot beverages like coffee or
            tea for the full drinking experience without collapsing or altering
            the drink. With no plastic barrier involved, the risk of
            microplastic release is eliminated.
          </p>
        </div>
      ),
    },
  ];

  const categories = ["All", "Innovation", "Business", "Technology"];
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredPosts =
    selectedCategory === "All"
      ? blogPosts
      : blogPosts.filter((post) => post.category === selectedCategory);

  const featuredPosts = blogPosts.filter(post => post.featured);

  return (
    <div className="blog-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <h1 className="display-medium">Blog & News</h1>
          <p className="body-large">
            Stay updated with the latest in edible packaging innovations and
            sustainable business practices.
          </p>
        </div>
      </section>
	  
	        {/* Museum Collaboration Section */}
      <section id="collaboration-section" className={`collaboration-section ${isCollaborationVisible ? 'visible' : ''}`}>
        <div className="container">
          <div className="collaboration-banner">
            <Sparkles className="banner-icon" size={24} />
            <span>Sustainability in Action</span>
          </div>
          
          <div className="collaboration-content">
            <div className="collaboration-text">
              <h2 className="collaboration-title">Magomeal × Home Computer Museum Helmond</h2>
              <p className="collaboration-description">
                We are happy to share that Magomeal is now collaborating with the Home Computer Museum Helmond.
              </p>
              <p className="collaboration-description">
                The museum has chosen to replace paper cups with our edible cups, helping reduce waste in a simple and fun way. Visitors can enjoy their drink and then eat the cup.
              </p>
              <p className="collaboration-description">
                We're proud to be part of this step toward more sustainable everyday choices and excited to work with a place that celebrates innovation.
              </p>
            </div>
            
            <div className="collaboration-logo">
              <a 
                href="https://www.homecomputermuseum.nl/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="museum-logo-link"
              >
                <img 
                  src={require('../assets/images/hcm_logo.png')} 
                  alt="Home Computer Museum Helmond Logo" 
                  className="museum-logo"
                />
                <span className="visit-museum">Visit Museum Website →</span>
              </a>
            </div>
          </div>
        </div>
      </section>
	  

      {/* Featured Post */}
{featuredPosts.length > 0 && (
  <section className="featured-post-section">
    <div className="container">
      {featuredPosts.map(post => (
        <div key={post.id} className="featured-post network-card">
          <div className="featured-badge">Featured Article</div>

          <div className="featured-content">
            <div className="post-category">{post.category}</div>
            <h2 className="heading-1">{post.title}</h2>
            <p className="body-large">{post.excerpt}</p>

            {expandedPostId === post.id && (
              <div className="post-content body-large">
                {post.content}
              </div>
            )}

            <div className="post-meta">
              <div className="meta-item">
                <User size={16} />
                <span className="body-small">{post.author}</span>
              </div>
              <div className="meta-item">
                <Calendar size={16} />
                <span className="body-small">{post.date}</span>
              </div>
              <div className="meta-item">
                <Clock size={16} />
                <span className="body-small">{post.readTime}</span>
              </div>
            </div>

            <button
              className="btn-primary"
              onClick={() => toggleExpand(post.id)}
            >
              {expandedPostId === post.id
                ? "Collapse Article"
                : "Read Full Article"}{" "}
              <ChevronRight size={20} />
            </button>
          </div>
        </div>
      ))}
    </div>
  </section>
)}


      {/* Blog Grid */}
      <section className="blog-posts-section">
        <div className="container">
          <div className="blog-posts-grid">
            {filteredPosts
              .filter((post) => !post.featured)
              .map((post) => (
                <article
                  key={post.id}
                  className="blog-post-card network-card hover-lift"
                >
                  <div className="post-header">
                    <div className="post-category">{post.category}</div>
                    <h3 className="network-card-title">{post.title}</h3>
                  </div>

                  <p className="network-card-content">{post.excerpt}</p>

                  {expandedPostId === post.id && (
                    <div className="post-content body-large">
                      {post.content}
                    </div>
                  )}

                  <div className="post-meta">
                    <div className="meta-item">
                      <User size={14} />
                      <span className="body-small">{post.author}</span>
                    </div>
                    <div className="meta-item">
                      <Calendar size={14} />
                      <span className="body-small">{post.date}</span>
                    </div>
                    <div className="meta-item">
                      <Clock size={14} />
                      <span className="body-small">
                        {post.readTime}
                      </span>
                    </div>
                  </div>

                  <div className="post-footer">
                    <button
                      className="read-more-btn"
                      onClick={() => toggleExpand(post.id)}
                    >
                      {expandedPostId === post.id
                        ? "Collapse Article"
                        : "Read Full Article"}{" "}
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </article>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
